import store from '@store';
import {
  getNestedDynamicFieldPayloadKey,
  getRootListingPayloadKey,
  isDynamicFieldNestedInPayload,
} from '../../../utility/dynamicFieldDestination';
import { shouldSendAreaListingFields } from '../../../utility/postListingPayloadArea';
import { isFurnishedYes } from '../../../helpers/furnishedPrefill';
import {
  assignRootDynamicListingField,
  createFormatDynamicFieldForApi,
  findOption,
  hasUsableDynamicFieldRaw,
  mergePreservedHiddenDynamicFields as mergePreservedHiddenDynamicFieldsShared,
  shouldSendVisibleOptionalFieldAsNull,
} from '../../common/payloads/dynamicFieldsPayloadUtils';
import { skipFieldsForField, showFieldsForPropertyType } from '../data/listing-form-data';

// Auto-translate must never null title/description on edit: nulling an existing value wipes it
// (596 listings were left with an empty title this way). Nulling is only safe on create.
const forceNullForAutoTranslatedApiField = (locale, apiKeyName, generateTitle, generateDescription, isEdit) => {
  if (isEdit) return false;
  if (locale === 'ar') {
    return (
      (apiKeyName === 'title' && generateTitle) ||
      (apiKeyName === 'description' && generateDescription)
    );
  }
  return (
    (apiKeyName === 'title_l1' && generateTitle) ||
    (apiKeyName === 'description_l1' && generateDescription)
  );
};

const resolveRentFrequencyIdForListingRoot = (raw, dynamicFieldDefinitions) => {
  if (raw === undefined || raw === null || raw === '') return raw;
  const def = dynamicFieldDefinitions?.find((d) => d.apiKeyName === 'rent_frequency');
  const opts = def?.options || [];
  if (!opts.length) return raw;
  const matched = findOption(opts, raw);
  return matched?.id != null ? matched.id : raw;
};

/** Resolve a scalar location id; avoid String(object) → "[object Object]". */
const coerceScalarLocationId = (v, depth = 0) => {
  if (v == null || v === '') return undefined;
  if (typeof v === 'string' || typeof v === 'number') return v;
  if (depth > 5 || typeof v !== 'object' || Array.isArray(v)) return undefined;
  return (
    coerceScalarLocationId(v.location_id, depth + 1) ??
    coerceScalarLocationId(v.id, depth + 1) ??
    coerceScalarLocationId(v.value, depth + 1)
  );
};

/** location-select: { city, location } on Formik `location-info`; ids may be on .value or values.location */
const resolveListingLocationIdFromComposite = (composite, values = {}) => {
  const fromListingRoot = () =>
    coerceScalarLocationId(values?.location) ??
    coerceScalarLocationId(values?.location?.id) ??
    coerceScalarLocationId(values?.location?.value);

  if (composite == null || composite === '') {
    return fromListingRoot();
  }

  const direct = coerceScalarLocationId(composite);
  if (direct != null && direct !== '') return direct;

  if (typeof composite !== 'object') return fromListingRoot();

  const loc = composite.location;
  const city = composite.city;
  const fromLoc =
    (loc && coerceScalarLocationId(loc)) ??
    coerceScalarLocationId(loc?.location_id) ??
    coerceScalarLocationId(loc?.id) ??
    coerceScalarLocationId(loc?.value);
  if (fromLoc != null && fromLoc !== '') return fromLoc;

  const fromCity =
    (city && coerceScalarLocationId(city)) ??
    coerceScalarLocationId(city?.location_id) ??
    coerceScalarLocationId(city?.id) ??
    coerceScalarLocationId(city?.value);
  if (fromCity != null && fromCity !== '') return fromCity;

  // Composite is a location field object (possibly cleared). Do not fall back to values.location —
  // that stays pre-filled from the listing and would resurrect a cleared selection in the payload.
  return undefined;
};

const formatDynamicFieldForApi = createFormatDynamicFieldForApi({
  resolveLocationId: resolveListingLocationIdFromComposite,
  locationInfoKeys: ['location-info'],
  slugPreferredKeys: ['sale_type', 'rent_frequency'],
  includeVideosAsListingAttributes: true,
});

const autoGeneratedListingContentPayload = ({ values, content_type, listingId, dynamicFieldDefinitions }) => {
  const isDailyRentalListing = values?.listing_purpose?.slug === 'daily-rental';
  const resolvedListingId = listingId ?? values?.id;
  const sendArea = shouldSendAreaListingFields(dynamicFieldDefinitions);
  return {
    content_type: content_type,
    purpose_name: values?.listing_purpose?.title,
    type: isDailyRentalListing
      ? values?.property_type_label
      : values?.sub_listing_type?.label || values?.listing_type?.title,
    city: values?.location?.city_id,
    location: values?.location?.title,
    ...(sendArea && {
      area: values?.area_unit?.value,
      area_unit: values?.area_unit?.id,
    }),
    furnished: isDailyRentalListing ? 'yes' : isFurnishedYes(values?.furnished) ? 'yes' : 'no',
    rental_frequency: isDailyRentalListing ? 1 : values?.listing_purpose?.title === 'Rent' && values?.rental_frequency,
    price:
      values?.listing_purpose?.title === 'Rent'
        ? values?.rental_price
          ? parseInt(values?.rental_price)
          : 0
        : values?.price
          ? parseInt(values?.price)
          : 0,
    beds: parseInt(values?.bedrooms) || null,
    baths: parseInt(values?.bathrooms) || null,
    ...(resolvedListingId != null &&
      resolvedListingId !== '' && { listing_id: String(resolvedListingId) }),
  };
};

const findFeatureById = (arr, featureId) => {
  for (const group of arr) {
    const feature = group.features.find((feature) => feature.feature_id == featureId);
    if (feature) {
      return feature?.id;
    }
  }
  return null;
};

const getListingFeatures = (featuresAmenities, listingFeatures) =>
  featuresAmenities && Object.keys(featuresAmenities)?.length
    ? Object.keys(featuresAmenities)
        .map((e) =>
          !!findFeatureById(listingFeatures, e)
            ? {
                id: findFeatureById(listingFeatures, e),
                feature_id: e,
                ...(featuresAmenities?.[e] == undefined || featuresAmenities?.[e] == false
                  ? { _destroy: true }
                  : { feature_value: featuresAmenities?.[e] === true ? 'Yes' : featuresAmenities?.[e] }),
              }
            : featuresAmenities?.[e]
              ? { feature_id: e, feature_value: featuresAmenities?.[e] === true ? 'Yes' : featuresAmenities?.[e] }
              : null,
        )
        .filter((e) => !!e)
    : [];

/**
 * Same rule as Oman (common/payloads/listings): listing root must not repeat keys that are sent under
 * dynamic_data.dynamic_fields. API `key_name` values come from /dynamic_fields (see dynamicFormFieldsMapper).
 */
const DYNAMIC_API_KEY_TO_LEGACY_LISTING_KEYS = {
  beds: ['beds'],
  bedrooms: ['beds'],
  baths: ['bath'],
  bathrooms: ['bath'],
  is_furnished: ['is_furnished'],
  furnished: ['is_furnished'],
  area_unit_value: ['area_unit_value', 'area_unit_id'],
  area: ['area_unit_value', 'area_unit_id'],
  features: ['listing_features_attributes'],
  feature_and_amenities: ['listing_features_attributes'],
  // Sale/rent legacy payload may set one or both; never duplicate when API owns pricing fields.
  price: ['price', 'rental_price'],
  rental_price: ['rental_price', 'price'],
  rent_frequency: ['rent_frequency_id'],
  rental_frequency: ['rent_frequency_id'],
  title: ['title', 'title_l1', 'title_translation_enabled'],
  title_l1: ['title', 'title_l1', 'title_translation_enabled'],
  description: ['description', 'description_l1', 'description_translation_enabled'],
  description_l1: ['description', 'description_l1', 'description_translation_enabled'],
  location_id: ['location_id'],
  /** API definitions use `location_info`; Formik uses `location-info` only — strip map is keyed by `apiKeyName`. */
  location_info: ['location_id'],
  property_images: ['listing_images_attributes'],
  images: ['listing_images_attributes'],
  videos: ['listing_videos_attributes'],
  completion_status: ['completion_status'],
  sale_type: ['sale_type'],
  additional_number: ['unit_no'],
  unit_variant: ['unit_variant'],
  national_address: ['national_address'],
  redirection_link: ['redirection_link'],
  residence_type: ['residence_type'],
  booked: ['booked'],
  additional_details: ['additional_details'],
};

const collectActiveDynamicApiKeys = (dynamicFieldDefinitions) => {
  const keys = new Set();
  (dynamicFieldDefinitions || []).forEach((def) => {
    const isVisible = def.display === true;
    const isHiddenRequired = def.display !== true && def.is_required === true;
    if (!isVisible && !isHiddenRequired) return;
    if (!isDynamicFieldNestedInPayload(def.destinationKey)) return;
    keys.add(def.apiKeyName);
  });
  return keys;
};

const stripListingKeysShadowedByDynamicFields = (listingObj, activeApiKeys) => {
  if (!activeApiKeys?.size) return;
  const legacyToRemove = new Set();
  activeApiKeys.forEach((apiKey) => {
    const mapped = DYNAMIC_API_KEY_TO_LEGACY_LISTING_KEYS[apiKey];
    if (mapped?.length) mapped.forEach((k) => legacyToRemove.add(k));
  });
  legacyToRemove.forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(listingObj, key)) {
      delete listingObj[key];
    }
  });
};

const mergePreservedHiddenDynamicFields = (
  dynamicFields,
  { values, listing, dynamicFieldDefinitions },
  includePreservedAreaUnit = true,
) => {
  mergePreservedHiddenDynamicFieldsShared(
    dynamicFields,
    { values, listing, dynamicFieldDefinitions },
    formatDynamicFieldForApi,
    includePreservedAreaUnit,
  );
};

/**
 * Map the form's currently-selected `property_type` (an internal category id) to its external_id
 * using the leaf children the edit response carries on `listing.listing_categories`. When a listing
 * loads on a non-leaf parent category (e.g. "Building") and the user re-picks a leaf sub-type, the
 * stale `listing.listing_category.external_id` must not win over the new selection — beds/baths skip
 * rules key off the property type's external_id, so the wrong id silently drops the user's edits.
 */
const resolveSelectedPropertyTypeExternalId = (values, listing) => {
  const internalId = values?.property_type;
  if (internalId == null || internalId === '') return null;
  const match = (listing?.listing_categories || []).find((c) => String(c?.id) === String(internalId));
  const ext = match?.external_id;
  if (ext == null || ext === '') return null;
  const n = Number(ext);
  return Number.isNaN(n) ? null : n;
};

const resolvePropertyTypeExternalId = (values, listing) => {
  // Prefer the user's current selection resolved to an external_id: the listing may have loaded on a
  // non-leaf parent category whose external_id no longer reflects the chosen leaf type.
  const fromSelection = resolveSelectedPropertyTypeExternalId(values, listing);
  if (fromSelection != null) return fromSelection;

  const fromListing =
    listing?.listing_category?.external_id ??
    listing?.listing_type?.external_id ??
    values?.sub_listing_type?.external_id ??
    values?.listing_type?.external_id;
  if (fromListing != null && fromListing !== '') {
    const n = Number(fromListing);
    if (!Number.isNaN(n)) return n;
  }
  const internalId =
    values?.property_type ?? listing?.listing_category?.id ?? listing?.listing_type?.id;
  if (internalId == null || internalId === '') return null;
  const n = Number(internalId);
  return Number.isNaN(n) ? null : n;
};

const isBedBathApiKey = (apiKeyName) => apiKeyName === 'beds' || apiKeyName === 'baths';

const isHiddenBedBathFieldForPropertyType = (apiKeyName, propertyTypeExternalId) => {
  if (propertyTypeExternalId == null || !isBedBathApiKey(apiKeyName)) return false;
  const formKey = apiKeyName === 'beds' ? 'bedrooms' : 'bathrooms';
  const rule = skipFieldsForField?.[formKey]?.property_type?.[propertyTypeExternalId];
  return rule === true || (rule && rule.skipField === true);
};

/**
 * Safe-by-default allow-list gate (total_floors / floor_number): the field must never be sent for a
 * property type outside its allow-list, even if a stale Formik value lingers from a prior property
 * type selection — sending it triggers a 422 for the whole save (dynamic_fields_validator rejects
 * unknown keys per category).
 */
const isHiddenShowFieldForPropertyType = (apiKeyName, propertyTypeExternalId) => {
  const allowList = showFieldsForPropertyType?.[apiKeyName];
  if (!allowList) return false;
  if (propertyTypeExternalId == null) return true;
  return !allowList.includes(propertyTypeExternalId);
};

/** Re-send beds/baths from edit API with slug intact (options fallback when BE omits slug). */
const formatSavedBedBathFromEdit = (savedField, options = []) => {
  if (savedField == null) return undefined;
  if (typeof savedField === 'object' && !Array.isArray(savedField)) {
    const value = savedField.value ?? savedField.label;
    if (value == null || value === '') return undefined;
    const matched =
      findOption(options, value) ??
      findOption(options, savedField.slug) ??
      findOption(options, savedField.value_l1);
    return {
      slug: savedField.slug ?? matched?.slug ?? String(value),
      value: String(value),
      value_l1: savedField.value_l1 ?? matched?.value_l1 ?? String(value),
    };
  }
  const matched = findOption(options, savedField);
  const scalar = String(savedField);
  return {
    slug: matched?.slug ?? scalar,
    value: scalar,
    value_l1: matched?.value_l1 ?? scalar,
  };
};

/** Edit update: prefer Formik when it resolves a slug; otherwise re-send the edit GET value. */
const formatBedBathForUpdatePayload = (
  def,
  values,
  listing,
  propertyTypeExternalId,
  forceNullForAutoTranslate,
) => {
  if (forceNullForAutoTranslate) return null;

  const savedField = listing?.dynamic_data?.dynamic_fields?.[def.apiKeyName];
  const hidden = isHiddenBedBathFieldForPropertyType(def.apiKeyName, propertyTypeExternalId);
  const fromForm = values[def.formKeyName];

  if (hidden && hasUsableDynamicFieldRaw(savedField)) {
    return formatSavedBedBathFromEdit(savedField, def.options);
  }

  if (hasUsableDynamicFieldRaw(fromForm)) {
    const formatted = formatDynamicFieldForApi(
      fromForm,
      def.data_type,
      def.apiKeyName,
      def.options,
      values,
      def.fieldSlug,
      def.destinationKey,
    );
    if (formatted?.slug != null) return formatted;
  }

  if (hasUsableDynamicFieldRaw(savedField)) {
    return formatSavedBedBathFromEdit(savedField, def.options);
  }

  if (hasUsableDynamicFieldRaw(fromForm)) {
    return formatSavedBedBathFromEdit(fromForm, def.options);
  }

  return undefined;
};

/**
 * Main loop skips `display: false` fields unless they are hidden-required with options.
 * On edit, re-send values from `listing.dynamic_data.dynamic_fields` (and Formik when hydrated)
 * so hidden fields are not dropped from the PUT body.
 * Also re-sends beds/baths from the edit response when hidden or Formik cannot resolve a slug.
 */
const mergeHiddenDynamicFieldsFromEditListing = (dynamicFields, rootDynamicFields, ctx) => {
  const { values, listing, dynamicFieldDefinitions } = ctx;
  const locale = store.getState().app.AppConfig.locale;
  const isEdit = listing != null;
  const saved = listing?.dynamic_data?.dynamic_fields;
  if (!dynamicFieldDefinitions?.length) return;
  const propertyTypeExternalId = resolvePropertyTypeExternalId(values, listing);

  dynamicFieldDefinitions.forEach((def) => {
    if (listing && isBedBathApiKey(def.apiKeyName)) {
      if (!isDynamicFieldNestedInPayload(def.destinationKey)) return;
      const nestedKey = getNestedDynamicFieldPayloadKey(def.destinationKey, def.apiKeyName);
      const forceNullForAutoTranslate = forceNullForAutoTranslatedApiField(
        locale,
        def.apiKeyName,
        values?.generate_title,
        values?.generate_description,
        isEdit,
      );
      const formatted = formatBedBathForUpdatePayload(
        def,
        values,
        listing,
        propertyTypeExternalId,
        forceNullForAutoTranslate,
      );
      if (formatted !== undefined) {
        dynamicFields[nestedKey] = formatted;
      }
      return;
    }

    if (def.display === true) return;

    const isHiddenRequiredWithOptions =
      def.display !== true && def.is_required === true && def.options?.length > 0;
    if (isHiddenRequiredWithOptions) {
      return;
    }

    const forceNullForAutoTranslate = forceNullForAutoTranslatedApiField(
      locale,
      def.apiKeyName,
      values?.generate_title,
      values?.generate_description,
      isEdit,
    );

    if (isDynamicFieldNestedInPayload(def.destinationKey)) {
      const nestedKey = getNestedDynamicFieldPayloadKey(def.destinationKey, def.apiKeyName);
      if (Object.prototype.hasOwnProperty.call(dynamicFields, nestedKey)) return;

      const fromForm = values[def.formKeyName];
      const raw = hasUsableDynamicFieldRaw(fromForm) ? fromForm : saved?.[def.apiKeyName];
      if (!hasUsableDynamicFieldRaw(raw)) return;

      const formatted = forceNullForAutoTranslate
        ? null
        : formatDynamicFieldForApi(
              raw,
              def.data_type,
              def.apiKeyName,
              def.options,
              values,
              def.fieldSlug,
              def.destinationKey,
            );
      if (formatted !== undefined) {
        dynamicFields[nestedKey] = formatted;
      }
    } else {
      const rootKey = getRootListingPayloadKey(def.destinationKey, def.apiKeyName);
      if (Object.prototype.hasOwnProperty.call(rootDynamicFields, rootKey)) return;

      const fromForm = values[def.formKeyName];
      const raw = hasUsableDynamicFieldRaw(fromForm) ? fromForm : saved?.[def.apiKeyName];
      if (!hasUsableDynamicFieldRaw(raw)) return;

      const formatted = forceNullForAutoTranslate
        ? null
        : formatDynamicFieldForApi(
            raw,
            def.data_type,
            def.apiKeyName,
            def.options,
            values,
            def.fieldSlug,
            def.destinationKey,
          );
      if (formatted !== undefined) {
        assignRootDynamicListingField(rootDynamicFields, def.destinationKey, def.apiKeyName, formatted);
      }
    }
  });
};

const getUpdateListingPayload = ({
  values,
  listing,
  autoRenew,
  productId,
  source,
  dynamicFieldDefinitions = [],
}) => {
  const locale = store.getState().app.AppConfig.locale;
  const isSellOrRentProperty = values?.listing_purpose?.slug == 'sale' || values?.listing_purpose?.slug == 'rent';
  const isDailyRentalListing = values?.listing_purpose?.slug == 'daily-rental';
  const isOffPlan = values?.is_offplan_listing;
  const isEdit = listing != null;
  const includeAreaListingFields = shouldSendAreaListingFields(dynamicFieldDefinitions);
  const propertyTypeExternalId = resolvePropertyTypeExternalId(values, listing);

  const dynamicFields = {};
  const rootDynamicFields = {};
  (dynamicFieldDefinitions || []).forEach((def) => {
    if (listing && isBedBathApiKey(def.apiKeyName)) return;
    if (isHiddenBedBathFieldForPropertyType(def.apiKeyName, propertyTypeExternalId)) return;
    if (isHiddenShowFieldForPropertyType(def.apiKeyName, propertyTypeExternalId)) return;

    const isVisible = def.display === true;
    const isHiddenRequired = def.display !== true && def.is_required === true;
    if (!isVisible && !isHiddenRequired) return;

    let raw;
    if (isVisible) {
      raw = values[def.formKeyName];
    } else if (isHiddenRequired && def.options?.length > 0) {
      const firstOption = def.options[0];
      const isMultiOrFeatures = def.data_type === 'multi-select' || def.apiKeyName === 'features';
      raw = isMultiOrFeatures ? [firstOption] : firstOption;
    } else {
      return;
    }
    const forceNullForAutoTranslate = forceNullForAutoTranslatedApiField(
      locale,
      def.apiKeyName,
      values?.generate_title,
      values?.generate_description,
      isEdit,
    );
    let formatted = forceNullForAutoTranslate
      ? null
      : formatDynamicFieldForApi(
          raw,
          def.data_type,
          def.apiKeyName,
          def.options,
          values,
          def.fieldSlug,
          def.destinationKey,
        );
    if (formatted === undefined) {
      // A displayed optional field the user cleared must be sent as `null` so the BE clears the
      // saved value, rather than omitting the key (which the BE reads as "keep existing value").
      if (!shouldSendVisibleOptionalFieldAsNull({ isVisible, isRequired: def.is_required, raw })) return;
      formatted = null;
    }
    if (isDynamicFieldNestedInPayload(def.destinationKey)) {
      const nestedKey = getNestedDynamicFieldPayloadKey(def.destinationKey, def.apiKeyName);
      dynamicFields[nestedKey] = formatted;
    } else {
      assignRootDynamicListingField(rootDynamicFields, def.destinationKey, def.apiKeyName, formatted);
    }
  });

  mergePreservedHiddenDynamicFields(dynamicFields, { values, listing, dynamicFieldDefinitions }, includeAreaListingFields);
  mergeHiddenDynamicFieldsFromEditListing(dynamicFields, rootDynamicFields, {
    values,
    listing,
    dynamicFieldDefinitions,
  });
  const fallbackDynamicFields = values?.dynamic_data?.dynamic_fields ?? listing?.dynamic_data?.dynamic_fields;
  const effectiveDynamicFields =
    Object.keys(dynamicFields).length > 0
      ? dynamicFields
      : fallbackDynamicFields && typeof fallbackDynamicFields === 'object'
        ? fallbackDynamicFields
        : null;

  const mergedDynamicFields = {
    ...(effectiveDynamicFields && typeof effectiveDynamicFields === 'object' ? effectiveDynamicFields : {}),
  };
  if (values?.additional_details != null && typeof values.additional_details === 'object') {
    mergedDynamicFields.additional_details = values.additional_details;
  }
  const hasMergedDynamicFields = Object.keys(mergedDynamicFields).length > 0;

  const typeId =
    values?.property_type ??
    (isSellOrRentProperty
      ? values?.sub_listing_type?.id || values?.listing_type?.id
      : values?.listing_type?.id);

  const listingPayload = {
    source,
    ...(typeId != null && { listing_category_id: typeId }),
    ...(values?.listing_type?.id &&
      !isDailyRentalListing &&
      typeId == null && { listing_category_id: values?.listing_type?.id }),
    latitude: values?.['location-info']?.map?.latitude || values?.latitude || values?.location?.latitude,
    longitude: values?.['location-info']?.map?.longitude || values?.longitude || values?.location?.longitude,
    ...(isSellOrRentProperty && {
      is_furnished: isFurnishedYes(values?.furnished),
    }),
    ...(includeAreaListingFields &&
      isSellOrRentProperty && {
        area_unit_id: values?.area_unit?.id,
      }),
    ...(includeAreaListingFields && {
      area_unit_value: isOffPlan || !isSellOrRentProperty ? values?.area : values?.area_unit?.value,
    }),
    ...(isDailyRentalListing && {
      national_address: values?.national_address,
      redirection_link: values?.redirection_link,
    }),
    ...(values?.residence_type_applicable && { residence_type: values?.residence_type }),
    listing_features_attributes: getListingFeatures(values?.feature_and_amenities, listing?.listing_features),
    beds: values?.bedrooms,
    bath: values?.bathrooms,
    ...(values?.property_discount_enabled
      ? (() => {
          // The original (REGA-registered) price is carried in `values.price` for sale, rent and
          // off-plan/project listings; send it as `actual_price` and the entered discounted value
          // as `price` (BE LP-5360).
          const baseNum = Number(values?.price);
          const actualPrice = Number.isFinite(baseNum) ? baseNum : null;
          const discountedNum =
            values?.discounted_price !== '' && values?.discounted_price != null
              ? Number(values.discounted_price)
              : null;
          const priceVal =
            discountedNum != null && Number.isFinite(discountedNum) ? discountedNum : null;
          return {
            actual_price: actualPrice,
            price: priceVal,
          };
        })()
      : {
          price: (values?.listing_purpose?.slug == 'sale' || isDailyRentalListing) ? values?.price : values?.rental_price,
          ...(values?.listing_purpose?.slug == 'rent' && { rental_price: values?.rental_price }),
        }),
    listing_images_attributes: formatDynamicFieldForApi(
      values?.property_images || [],
      'multi-select',
      'images',
      [],
      values,
      'images',
      'listing_images_attributes',
    ),
    listing_videos_attributes: values?.videos?.length
      ? values?.videos
          .filter((e) => e.id || (!e.id && !e._destroy))
          .map((e) => ({
            ...(e.id && { id: e.id }),
            video_host: 'youtube',
            video_title: '',
            video_link: e?.url,
            ...(e._destroy && { _destroy: e._destroy }),
          }))
      : [],
    title: !isEdit && values?.generate_title && locale === 'ar'
      ? null
      : values?.property_title_en ?? listing?.title ?? '',
    title_l1: !isEdit && values?.generate_title && locale !== 'ar'
      ? null
      : values?.property_title_ar ?? listing?.title_l1 ?? '',
    description: !isEdit && values?.generate_description && locale === 'ar'
      ? null
      : values?.property_description_en ?? listing?.description ?? '',
    description_l1: !isEdit && values?.generate_description && locale !== 'ar'
      ? null
      : values?.property_description_ar ?? listing?.description_l1 ?? '',
    title_translation_enabled: values?.generate_title,
    description_translation_enabled: values?.generate_description,
    platform_id: 1,
    ...(isSellOrRentProperty && {
      rent_frequency_id: resolveRentFrequencyIdForListingRoot(values?.rental_frequency, dynamicFieldDefinitions),
    }),
    is_autorenewal_on: autoRenew,
    location_id: values?.['location-info']?.location?.location_id
      ? values?.['location-info']?.location?.location_id
      : values?.['location-info']?.city?.id,
    booked: values?.booked,
    ...(isOffPlan && {
      unit_no: values?.additional_number,
      sale_type: values?.sale_type || null,
      listing_category_id: values?.property_type,
      unit_variant: values?.unit_variant,
      completion_status: values?.completion_status,
    }),
    ...(hasMergedDynamicFields && {
      dynamic_data: { dynamic_fields: mergedDynamicFields },
    }),
  };

  Object.assign(listingPayload, rootDynamicFields);

  stripListingKeysShadowedByDynamicFields(listingPayload, collectActiveDynamicApiKeys(dynamicFieldDefinitions));

  // Discount pricing is owned by the curated `actual_price`/`price` block above. The dynamic-field
  // serializer (Object.assign of rootDynamicFields) re-injects the raw `price` — clobbering the
  // discounted value back to the actual price — and leaks the FE-only `discounted_price` /
  // `property_discount_enabled` form fields. Re-assert the two-key contract here (sale, rent and
  // off-plan/project — all carry the original in `values.price`):
  //   actual_price = original listing price, price = discounted price.
  if (values?.property_discount_enabled) {
    const baseNum = Number(values?.price);
    const discountedNum =
      values?.discounted_price !== '' && values?.discounted_price != null
        ? Number(values.discounted_price)
        : null;
    listingPayload.actual_price = Number.isFinite(baseNum) ? baseNum : null;
    listingPayload.price = discountedNum != null && Number.isFinite(discountedNum) ? discountedNum : null;
  }

  // FE-only fields: BE derives the discount by comparing actual_price vs price and returns
  // `discount_applied`. These must never be sent.
  delete listingPayload.discounted_price;
  delete listingPayload.property_discount_enabled;

  return {
    listing: listingPayload,
    product_id: productId,
  };
};

export default { autoGeneratedListingContentPayload, getUpdateListingPayload };
